import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Stack, Group, Text, Button, SimpleGrid } from '@repletoryreach/sdk-ui';
import {
  Alert,
  EmptyState,
  KpiFilterRow,
  PageHeader,
  TableShell,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { admin, isApiError } from '@repletoryreach/sdk-api';
import { FilterDrawer, type FilterField, type FilterValues } from '@repletoryreach/sdk-ui/data-display';
import { StatCard } from '../../components/StatCard';
import { TableFooter } from '../../components/TableFooter';
import { ActiveFilterPills, type ActiveFilterPill } from '../../components/ActiveFilterPills';
import { usePersistedState } from '../../lib/usePersistedState';
import { formatInteger, formatRelative } from '../../lib/format';
import { useT } from '../../i18n/I18nProvider';
import { QueuesTable } from './components/QueuesTable';
import { QueuesList } from './components/QueuesList';
import { QueuesCards } from './components/QueuesCards';
import { QueueDetailDrawer } from './components/QueueDetailDrawer';
import { RequeueDialog } from './components/RequeueDialog';
import { OpsAuditPanel } from './components/OpsAuditPanel';
import { BACKLOG_WARN, queueKey, type QueueSamples } from './components/queueState';

const MUTED = 'var(--rr-color-text-muted)';
const REFRESH_MS = 10_000;

const StackIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3.5l8.5 4-8.5 4-8.5-4z" /><path d="M3.5 12l8.5 4 8.5-4" /><path d="M3.5 16.5l8.5 4 8.5-4" />
  </svg>
);
const InboxIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3.5 13.5h5l1.5 3h4l1.5-3h5" /><path d="M6 4.5h12l2.5 9v6h-17v-6z" />
  </svg>
);
const HourglassIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M7 3.5h10M7 20.5h10" /><path d="M7.5 3.5c0 4 4.5 5 4.5 8.5s-4.5 4.5-4.5 8.5" /><path d="M16.5 3.5c0 4-4.5 5-4.5 8.5s4.5 4.5 4.5 8.5" />
  </svg>
);
const HISTORY_POINTS = 30;

type Queue = admin.QueueStat;
type KpiKey = 'all' | 'running' | 'backlogged' | 'no_consumers' | 'unacked';

export default function QueueMonitoringPage() {
  const t = useT();
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'ops', 'queues'],
    queryFn: () => admin.listQueues(),
    refetchInterval: REFRESH_MS
  });
  const all = useMemo(() => (data?.data ?? []) as Queue[], [data]);
  const brokerError = data?.meta?.error;
  const brokerStatus = data?.meta?.status;
  const brokerMessage = data?.meta?.message;
  const denied = isApiError(error) && error.status === 403;

  const [view, setView] = usePersistedState<ViewMode>('admin.queues.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.queues.pageSize', 25);

  const [query, setQuery] = useState('');
  const [kpi, setKpi] = useState<KpiKey | null>('all');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);
  // Which queue the detail panel is showing, by its vhost-scoped key.
  const [selected, setSelected] = useState<string | null>(null);
  // Which queue the requeue dialog is acting on. Held here rather than in the
  // table so the drawer opens the same dialog, and held as the row itself
  // rather than as a key: looking the row up in a list that polls every ten
  // seconds would close the dialog, and discard the receipt of what was just
  // moved, the moment the broker hiccuped and answered with an empty list.
  const [requeueing, setRequeueing] = useState<Queue | null>(null);

  const counts = useMemo(() => ({
    all: all.length,
    running: all.filter((r) => r.state === 'running').length,
    backlogged: all.filter((r) => (r.messages_ready ?? 0) > BACKLOG_WARN).length,
    no_consumers: all.filter((r) => (r.consumers ?? 0) === 0 && (r.messages ?? 0) > 0).length,
    unacked: all.filter((r) => (r.messages_unacked ?? 0) > 0).length
  }), [all]);

  const kpiItems: KpiFilterItem[] = [
    { id: 'all', label: t('queues.kpi.all'), value: counts.all, tone: 'primary' },
    { id: 'running', label: t('status.running'), value: counts.running, tone: 'healthy' },
    { id: 'backlogged', label: t('queues.kpi.backlogged', { threshold: BACKLOG_WARN }), value: counts.backlogged, tone: 'warning' },
    { id: 'no_consumers', label: t('queues.kpi.noConsumers'), value: counts.no_consumers, tone: 'down' },
    { id: 'unacked', label: t('queues.columns.unacked'), value: counts.unacked, tone: 'pending' }
  ];

  const filterFields: FilterField[] = useMemo(() => {
    const states = Array.from(new Set(all.map((r) => r.state).filter(Boolean))).sort() as string[];
    const vhosts = Array.from(new Set(all.map((r) => r.vhost).filter(Boolean))).sort() as string[];
    return [
      { key: 'state', label: t('queues.columns.state'), placeholder: t('queues.filters.anyState'), options: states.map((s) => ({ value: s, label: s })) },
      { key: 'vhost', label: t('queues.columns.vhost'), placeholder: t('queues.filters.anyVhost'), options: vhosts.map((v) => ({ value: v, label: v })) }
    ];
  }, [all, t]);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters = Boolean(query.trim()) || (kpi !== null && kpi !== 'all') || drawerActive > 0;
  function clearAllFilters() { setQuery(''); setKpi('all'); setFilters({}); setPage(1); }

  const filtered = useMemo(() => {
    let rows = all;
    if (kpi === 'running') rows = rows.filter((r) => r.state === 'running');
    if (kpi === 'backlogged') rows = rows.filter((r) => (r.messages_ready ?? 0) > BACKLOG_WARN);
    if (kpi === 'no_consumers') rows = rows.filter((r) => (r.consumers ?? 0) === 0 && (r.messages ?? 0) > 0);
    if (kpi === 'unacked') rows = rows.filter((r) => (r.messages_unacked ?? 0) > 0);
    if (filters.state) rows = rows.filter((r) => r.state === filters.state);
    if (filters.vhost) rows = rows.filter((r) => r.vhost === filters.vhost);
    const q = query.trim().toLowerCase();
    if (q) rows = rows.filter((r) => queueKey(r).toLowerCase().includes(q));
    return rows;
  }, [all, kpi, filters, query]);

  // The metric cards describe the rows on screen, because the filtered count
  // sits directly beneath them and two different denominators next to each
  // other read as a bug.
  const backlog = filtered.reduce((sum, q) => sum + (q.messages_ready ?? 0), 0);
  const unackedTotal = filtered.reduce((sum, q) => sum + (q.messages_unacked ?? 0), 0);
  // The unfiltered figures, so a filtered card can say what it is a slice of
  // rather than leaving the operator to guess at the denominator.
  const platformBacklog = all.reduce((sum, q) => sum + (q.messages_ready ?? 0), 0);
  const platformUnacked = all.reduce((sum, q) => sum + (q.messages_unacked ?? 0), 0);

  // A queue's depth matters far less than its direction, and the broker gives
  // only the current instant. Keeping the last few polls in a ref turns three
  // numbers into three trends without any server change.
  const history = useRef<{ queues: number[]; backlog: number[]; unacked: number[] }>({ queues: [], backlog: [], unacked: [] });
  const lastRecorded = useRef(0);
  // The same idea per queue: the broker reports an instant, and whether a
  // queue is filling or draining is the thing an operator is actually reading
  // for. State rather than a ref, because the table renders from it.
  const [queueHistory, setQueueHistory] = useState<Record<string, QueueSamples>>({});
  useEffect(() => {
    // One point per poll, keyed on the poll's own timestamp, so a re-render
    // caused by a filter change does not fabricate a data point.
    if (!dataUpdatedAt || dataUpdatedAt === lastRecorded.current) return;
    lastRecorded.current = dataUpdatedAt;
    const h = history.current;
    h.queues = [...h.queues, filtered.length].slice(-HISTORY_POINTS);
    h.backlog = [...h.backlog, backlog].slice(-HISTORY_POINTS);
    h.unacked = [...h.unacked, unackedTotal].slice(-HISTORY_POINTS);
    setQueueHistory((prev) => {
      const next: Record<string, QueueSamples> = {};
      for (const q of all) {
        const key = queueKey(q);
        const before = prev[key] ?? { ready: [], unacked: [], published: [], delivered: [], at: [] };
        next[key] = {
          ready: [...before.ready, q.messages_ready ?? 0].slice(-HISTORY_POINTS),
          unacked: [...before.unacked, q.messages_unacked ?? 0].slice(-HISTORY_POINTS),
          // The counters are what a real rate is worked out from: the broker's
          // own rate is an average over a window it does not tell us about.
          published: [...before.published, q.published_total ?? 0].slice(-HISTORY_POINTS),
          delivered: [...before.delivered, q.delivered_total ?? 0].slice(-HISTORY_POINTS),
          at: [...before.at, dataUpdatedAt].slice(-HISTORY_POINTS)
        };
      }
      return next;
    });
  }, [dataUpdatedAt, filtered.length, backlog, unackedTotal, all]);

  useEffect(() => { if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1); }, [filtered.length, page, pageSize]);
  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  const failed = Boolean(error);
  const metric = (value: number) => (failed ? t('common.notAvailable') : formatInteger(value));

  const pills: ActiveFilterPill[] = [];
  if (query.trim()) pills.push({ id: 'query', field: t('common.search'), value: query.trim() });
  if (kpi && kpi !== 'all') {
    pills.push({ id: 'kpi', field: t('queues.columns.state'), value: kpiItems.find((k) => k.id === kpi)?.label ?? kpi });
  }
  if (filters.state) pills.push({ id: 'state', field: t('queues.columns.state'), value: filters.state });
  if (filters.vhost) pills.push({ id: 'vhost', field: t('queues.columns.vhost'), value: filters.vhost });

  function removePill(id: string) {
    setPage(1);
    if (id === 'query') { setQuery(''); return; }
    if (id === 'kpi') { setKpi('all'); return; }
    setFilters((prev) => { const next = { ...prev }; delete next[id]; return next; });
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('queues.title')}
        description={t('queues.description', { seconds: REFRESH_MS / 1000, threshold: BACKLOG_WARN })}
        actions={
          <Group gap="sm">
            <Text role="caption" c={MUTED}>
              {dataUpdatedAt > 0
                ? t('queues.lastUpdated', { when: formatRelative(new Date(dataUpdatedAt).toISOString()), seconds: REFRESH_MS / 1000 })
                : t('queues.autoRefresh', { seconds: REFRESH_MS / 1000 })}
            </Text>
            <Button size="sm" variant="default" onClick={() => refetch()} loading={isFetching}>{t('action.refresh')}</Button>
          </Group>
        }
      />

      {denied && (
        <Alert color="warning" title={t('queues.denied.title')}>{t('queues.denied.body')}</Alert>
      )}

      {/* The endpoint answers 200 with an empty list when RabbitMQ itself is
          unreachable, so the request succeeded and the platform did not: every
          queued job is stalled behind this, which is why it is red rather than
          amber. The HTTP status and exception text tell the operator whether
          it is auth, DNS or a dead broker. */}
      {brokerError && (
        <Alert color="error" title={t('queues.unreachableTitle')}>
          <Stack gap="xs">
            <Text role="bodySmall">{t('queues.unreachable', { error: brokerError })}</Text>
            {(brokerStatus || brokerMessage) && (
              <Text role="caption" c={MUTED}>
                {t('queues.unreachableDetail', {
                  status: brokerStatus ?? t('common.notAvailable'),
                  message: brokerMessage ?? t('common.notAvailable')
                })}
              </Text>
            )}
          </Stack>
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <StatCard
          icon={StackIcon}
          tone="primary"
          label={hasActiveFilters ? t('queues.metrics.queuesFiltered') : t('queues.metrics.queues')}
          value={metric(filtered.length)}
          hint={hasActiveFilters ? t('queues.metrics.platformTotal', { count: formatInteger(all.length) }) : t('queues.metrics.queuesHint')}
          sparkline={history.current.queues}
          loading={isLoading}
          withRule
        />
        <StatCard
          icon={InboxIcon}
          tone={backlog > BACKLOG_WARN ? 'warning' : 'success'}
          label={hasActiveFilters ? t('queues.metrics.readyBacklogFiltered') : t('queues.metrics.readyBacklog')}
          value={metric(backlog)}
          hint={hasActiveFilters
            ? t('queues.metrics.platformTotal', { count: formatInteger(platformBacklog) })
            : t('queues.metrics.readyHint')}
          sparkline={history.current.backlog}
          loading={isLoading}
          withRule
        />
        <StatCard
          icon={HourglassIcon}
          tone={unackedTotal > 0 ? 'warning' : 'neutral'}
          label={hasActiveFilters ? t('queues.metrics.unackedFiltered') : t('queues.metrics.unackedTotal')}
          value={metric(unackedTotal)}
          hint={hasActiveFilters
            ? t('queues.metrics.platformTotal', { count: formatInteger(platformUnacked) })
            : t('queues.metrics.unackedHint')}
          sparkline={history.current.unacked}
          loading={isLoading}
          withRule
        />
      </SimpleGrid>

      <TableShell
        title={t('queues.tableTitle')}
        totalLabel={t('queues.totalLabel', { shown: filtered.length, total: all.length })}
        kpis={<KpiFilterRow items={kpiItems} value={kpi} onChange={(next) => { setKpi((next ?? 'all') as KpiKey); setPage(1); }} />}
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('queues.searchPlaceholder')}
        filters={
          <FilterDrawer fields={filterFields} values={filters} onApply={(next) => { setFilters(next); setPage(1); }} />
        }
        belowToolbar={<ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />}
        view={view}
        onViewChange={setView}
        loading={isLoading}
        isEmpty={!isLoading && !failed && filtered.length === 0}
        empty={
          <EmptyState
            title={t('queues.empty')}
            description={hasActiveFilters ? t('common.tryClearingFilters') : t('queues.emptyRows')}
            action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined}
          />
        }
        error={failed && !denied ? (
          <Alert color="error">
            <Group justify="space-between" align="center" style={{ width: '100%' }}>
              <span>{t('queues.loadError')}</span>
              <Button size="xs" variant="outline" onClick={() => refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        ) : null}
        pagination={
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
          />
        }
      >
        <>
          {view === 'table' && (
            <QueuesTable
              rows={paged}
              loading={isLoading}
              history={queueHistory}
              onOpen={(q) => setSelected(queueKey(q))}
              onRequeue={setRequeueing}
            />
          )}
          {view === 'list' && <QueuesList rows={paged} />}
          {view === 'cards' && <QueuesCards rows={paged} />}
        </>
      </TableShell>

      {/* Every move of customer messages this console has made. It belongs on
          the page rather than behind a link: the operator about to move more
          should see what was already moved, by whom and why. */}
      <OpsAuditPanel />

      <QueueDetailDrawer
        queue={selected ? all.find((q) => queueKey(q) === selected) ?? null : null}
        history={selected ? queueHistory[selected] : undefined}
        onClose={() => setSelected(null)}
        onRequeue={setRequeueing}
      />

      {/* After the drawer, so its portal paints above one that is still open:
          both overlays share the --rr-z-modal layer. */}
      <RequeueDialog
        queue={requeueing}
        // The snapshot keeps the dialog open; the depth still comes from the
        // latest poll, so the confirmation counts against a current figure.
        held={requeueing
          ? all.find((q) => queueKey(q) === queueKey(requeueing))?.messages ?? requeueing.messages ?? 0
          : 0}
        onClose={() => setRequeueing(null)}
        onMoved={() => refetch()}
      />
    </Stack>
  );
}
